import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";

const ProjectInfoModal = ({
  isOpen,
  onClose,
  project,
  onApprove,
  onReject,
  statusType = null,
  showAnimation,
  setShowAnimation,
  user,
}) => {
  const [hasPrivileges, setHasPrivileges] = useState(false);
  useEffect(() => {
    if (isOpen && statusType) {
      setShowAnimation(true);
      const timer = setTimeout(() => setShowAnimation(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, statusType]);

  if (!isOpen) return null;

  const isResultModal = ['approved', 'rejected', 'banned'].includes(statusType);

  // Result modal styles
  const statusConfig = {
    approved: {
      icon: (
        <CheckCircle2
          className={`h-16 w-16 text-green-500 ${
            showAnimation ? 'scale-125' : 'scale-100'
          } transition-all duration-700`}
        />
      ),
      title: "Project Approved",
      description:
        "The project has been successfully approved and is now publicly visible.",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      buttonColor: "bg-green-600 hover:bg-green-700",
    },
    rejected: {
      icon: (
        <XCircle
          className={`h-16 w-16 text-red-500 ${
            showAnimation ? 'scale-125' : 'scale-100'
          } transition-all duration-700`}
        />
      ),
      title: "Project Rejected",
      description:
        "The project has been rejected. The user will be notified with your feedback.",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      buttonColor: "bg-red-600 hover:bg-red-700",
    },
    banned: {
      icon: (
        <ShieldAlert
          className={`h-16 w-16 text-orange-500 ${
            showAnimation ? 'scale-125 rotate-12' : 'scale-100 rotate-0'
          } transition-all duration-700`}
        />
      ),
      title: "Project Banned",
      description:
        "The project has been banned and is no longer accessible. This action has been logged.",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      buttonColor: "bg-orange-600 hover:bg-orange-700",
    },
  };

  if (!isResultModal) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      >
        <div className="bg-white rounded-lg shadow-xl p-6 w-96 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
          >
            <X />
          </button>
          <h2 className="text-2xl font-bold mb-4">Project Details</h2>

          <div className="space-y-3">
            <div>
              <p className="font-semibold text-gray-600">Project Name</p>
              <p>{project?.name || "Untitled"}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-600">Client</p>
              <p>{project?.user?.name || "Unknown"}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-600">Submission Date</p>
              <p>{project?.updated_at || "N/A"}</p>
            </div>




       {user.role_id != 'supervisor' && (

            <div className="mt-6 flex justify-between space-x-4">
              <button
                onClick={onApprove}
                className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center"
              >
                ✓ Approve
              </button>
              <button
                onClick={onReject}
                className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center"
              >
                ✗ Reject
              </button>
            </div>
      )}
          </div>
        </div>
      </motion.div>
    );
  }

  const config = statusConfig[statusType];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`${config.bgColor} border ${config.borderColor} rounded-lg shadow-xl p-6 w-96 max-w-full mx-4 transform transition-all duration-300 ease-in-out`}
      >
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-white hover:bg-opacity-50 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
          </button>
        </div>

        <div className="flex flex-col items-center text-center pt-2 pb-6">
          <div className={`mb-4 ${showAnimation ? 'animate-pulse' : ''}`}>
            {config.icon}
          </div>

          <h2 className="text-xl font-bold mb-2">{config.title}</h2>
          <p className="text-gray-600">{config.description}</p>

          <div className="mt-6 flex space-x-3">
          <button
            className={`px-6 py-2 text-white   rounded-md transition-all ${config.buttonColor}`}
            onClick={onClose}
          >
            Close
          </button>

          {user.role_id === 'admin' && (
              <>
                {statusType === "approved" && (
                  <button
                    className="px-6 py-2 text-white rounded-md transition-all bg-orange-600 hover:bg-orange-700"
                    onClick={onReject}
                  >
                    Reject
                  </button>
                )}

                {statusType === "rejected" && (
                  <button
                    className="px-6 py-2 text-white rounded-md transition-all bg-green-600 hover:bg-green-700"
                    onClick={onApprove}
                  >
                    Approve
                  </button>
                )}
              </>

          )}

        </div>

        </div>
      </div>
    </div>
  );
};

export default ProjectInfoModal;
